import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Paper,
  Divider,
  Stack,
  TextField,
  Button,
  Snackbar,
  Alert,

  CircularProgress,
  Chip,
 
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  AccessTime as ClockIcon,
  People as PeopleIcon, 
  Warning as AlertIcon,
  TrendingUp as TrendingUpIcon,
  LocationOn as LocationIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Info as InfoIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  ErrorOutline as RiskIcon,
  FilterList as FilterIcon,
  Public as GlobalIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useAuthToken } from "../TokenContext";
import axios from 'axios';
import './Globallens.css';

// Import the new components
import GlobeVisualization from './InteractiveGlobe';
import { enhanceLocationWithGeoData } from './enhancedLocationUtils';

// Mock data generators for milestones and risks
// ✅ Constants OUTSIDE functions (created once)
const MILESTONE_TYPES = [
  "Product Launch",
  "System Migration",
  "Infrastructure Upgrade",
  "Market Expansion",
  "Compliance Deadline",
  "Team Onboarding",
  "Software Release"
];

const MILESTONE_TIMEFRAMES = [
  "Next Week",
  "2 Weeks",
  "1 Month",
  "Q2 2023",
  "Q3 2023"
];

const MILESTONE_STATUSES = [
  { label: "On Track", color: "#10B981" },
  { label: "At Risk", color: "#F59E0B" },
  { label: "Delayed", color: "#EF4444" },
  { label: "Planning", color: "#3B82F6" }
];

const RISK_TYPES = [
  "Security Breach",
  "Resource Shortage",
  "Vendor Outage",
  "Compliance Issue",
  "Technical Debt",
  "Talent Retention",
  "Market Volatility"
];

const RISK_SEVERITIES = [
  { label: "Low", color: "#10B981" },
  { label: "Medium", color: "#F59E0B" }, 
  { label: "High", color: "#EF4444" }
];

const RISK_IMPACTS = [
  "Operations", 
  "Customer Experience", 
  "Financial", 
  "Reputation"
];

// ✅ Functions that USE the constants (not recreate them)
const generateMockMilestones = () => {
  const count = Math.floor(Math.random() * 3) + 2;
  const milestones = [];
  
  for (let i = 0; i < count; i++) {
    const type = MILESTONE_TYPES[Math.floor(Math.random() * MILESTONE_TYPES.length)];
    const timeframe = MILESTONE_TIMEFRAMES[Math.floor(Math.random() * MILESTONE_TIMEFRAMES.length)];
    const status = MILESTONE_STATUSES[Math.floor(Math.random() * MILESTONE_STATUSES.length)];
    
    milestones.push({
      id: `ms-${i}`,
      name: `${type} ${i+1}`,
      description: `${type} planned for ${timeframe}`,
      dueDate: timeframe,
      status: status.label,
      statusColor: status.color
    });
  }
  
  return milestones;
};

const generateMockRisks = () => {
  const count = Math.floor(Math.random() * 3) + 1;
  const risks = [];
  
  for (let i = 0; i < count; i++) {
    const type = RISK_TYPES[Math.floor(Math.random() * RISK_TYPES.length)];
    const severity = RISK_SEVERITIES[Math.floor(Math.random() * RISK_SEVERITIES.length)];
    const impact = RISK_IMPACTS[Math.floor(Math.random() * RISK_IMPACTS.length)];
    
    risks.push({
      id: `risk-${i}`,
      name: type,
      description: `Potential ${impact.toLowerCase()} impact due to ${type.toLowerCase()}`,
      severity: severity.label,
      severityColor: severity.color,
      impact: impact,
      mitigationPlan: `${type} mitigation strategy in progress`
    });
  }
  
  return risks;
};

// API endpoints
const API_ENDPOINTS = {
  CREATE_LOCATION: process.env.REACT_APP_CLIENT_CREATEGLOBALLOCATIONSMANAGER,
  FETCH_LOCATIONS:process.env.REACT_APP_CLIENT_FETCHGLOBALLOCATIONSMANAGER,
  EDIT_LOCATION:process.env.REACT_APP_CLIENT_EDITGLOBALLOCATIONSMANAGER,
  DELETE_LOCATION: process.env.REACT_APP_CLIENT_DELETEGLOBALMANAGER
};
const GlobalLens = () => {
  const [locations, setLocations] = useState([]);
  const [currentTime, setCurrentTime] = useState({});
  const token = useAuthToken();
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [newLocationOpen, setNewLocationOpen] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [orgCode, setOrgCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useSelector(state => state.auth);
  const [searchValue, setSearchValue] = useState('');
  const [helpOpen, setHelpOpen] = useState(false);
  
  // State for view filtering
  const [viewMode, setViewMode] = useState('global');
  const [selectedFilterLocation, setSelectedFilterLocation] = useState(null);
 
  const [editLocationOpen, setEditLocationOpen] = useState(false);
  const [editLocation, setEditLocation] = useState({
    Gigs_id: '',
    Gigs_name: '',
    Organizational_Code: ''
  });
  // Form state for new location
  const [newLocation, setNewLocation] = useState({
    global_location_name: '',
    Organizational_Code: ''
  });

  // Initialize with the user's client code
  useEffect(() => {
    if (user?.data?.length > 0 && user.data[0].client_code) {
      const clientCode = user.data[0].client_code;
      setOrgCode(clientCode);
      
      // Fetch locations immediately if we have the client code
      fetchLocations(clientCode);
    }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.data, token]);

  // Update current time for each location
  useEffect(() => {
    const timer = setInterval(() => {
      const times = {};
      locations.forEach(location => {
        try {
          const time = new Date().toLocaleTimeString('en-US', {
            timeZone: location.timezone,
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          });
          times[location.name] = time;
        } catch (e) {
          // Fallback to local time if there's an error with the timezone
          times[location.name] = new Date().toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          });
        }
      });
      setCurrentTime(times);
    }, 1000);
    
    return () => clearInterval(timer);
  }, [locations]);

  // Group locations by country for the expansion UI
  

  // Fetch locations from API with improved error handling
  const fetchLocations = async (organizationalCode) => {
    if (!organizationalCode) {
      setNotification({
        open: true,
        message: 'Please enter an organization code',
        severity: 'warning'
      });
      return;
    }
    
    setIsLoading(true);
    try {
      // Make sure token is available and properly formatted
      // if (!token) {
      //   console.error('Authorization token is missing');
      //   setNotification({
      //     open: true,
      //     message: 'Authorization token is missing. Please log in again.',
      //     severity: 'error'
      //   });
      //   return;
      // }

      // Ensure token is properly formatted with Bearer prefix if needed
      // const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      
      const response = await axios.post(API_ENDPOINTS.FETCH_LOCATIONS, {
        Organization_Code: organizationalCode
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log("API Response:", response.data); // Log the response for debugging
      
      // Handle the specific response format from your API
      if (response.data && 
          response.data.body && 
          response.data.body.body && 
          Array.isArray(response.data.body.body)) {
        
        // Extract the locations array from the nested structure
        const locationsData = response.data.body.body;
        
        if (locationsData.length === 0) {
          setNotification({
            open: true,
            message: 'No locations found for this organization code',
            severity: 'info'
          });
          setLocations([]);
          return;
        }
        
        // Enhance locations with geographical information
        const enhancedLocations = locationsData.map((loc, index, array) => {
          // Use our enhanced location mapper with geographical coordinates
          const mappedLoc = enhanceLocationWithGeoData(loc, index, array.length);
          
          // Add mock milestones and risks
          mappedLoc.milestones = generateMockMilestones();
          mappedLoc.risks = generateMockRisks();
          
          return mappedLoc;
        });
        
        setLocations(enhancedLocations);
        
        // Show success notification
        setNotification({
          open: true,
          message: `Successfully loaded ${enhancedLocations.length} locations`,
          severity: 'success'
        });
      } else {
        console.warn('Unexpected locations response format:', response.data);
        setNotification({
          open: true,
          message: 'No locations found or unexpected response format',
          severity: 'info'
        });
        setLocations([]);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      // setNotification({
      //   open: true,
      //   message: `Error fetching locations: ${error.message}`,
      //   severity: 'error'
      // });
      setLocations([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Create new location with enhanced validation and token handling
  const createLocation = async () => {
    // Validate form
    if (!newLocation.global_location_name) {
      setNotification({
        open: true,
        message: 'Please enter a location name',
        severity: 'warning'
      });
      return;
    }
    
    if (!newLocation.Organizational_Code) {
      setNotification({
        open: true,
        message: 'Please enter an organization code',
        severity: 'warning'
      });
      return;
    }
    
    // Make sure token is available
    if (!token) {
      console.error('Authorization token is missing');
      setNotification({
        open: true,
        message: 'Authorization token is missing. Please log in again.',
        severity: 'error'
      });
      return;
    }

    // Ensure token is properly formatted with Bearer prefix if needed
    // const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    
    setIsLoading(true);
    try {
      // Prepare payload with all required fields
      const payload = {
        global_location_name: newLocation.global_location_name,
        Organizational_Code: newLocation.Organizational_Code
      };
      
      const response = await axios.post(API_ENDPOINTS.CREATE_LOCATION, payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data) {
        setNotification({
          open: true,
          message: 'Location created successfully',
          severity: 'success'
        });
        
        // Refresh locations list
        fetchLocations(newLocation.Organizational_Code);
        
        // Close the modal
        setNewLocationOpen(false);
        
        // Reset form
        setNewLocation({
          global_location_name: '',
          Organizational_Code: ''
        });
      } else {
        throw new Error('No data returned from API');
      }
    } catch (error) {
      console.error('Error creating location:', error);
      setNotification({
        open: true,
        message: `Error creating location: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Get filtered locations based on search, view mode and selected location
  const getFilteredLocations = () => {
    let filteredLocs = locations;
    
    // First apply view mode filtering
    if (viewMode === 'location' && selectedFilterLocation) {
      filteredLocs = locations.filter(loc => 
        loc.id === selectedFilterLocation.id || 
        loc.countryCode === selectedFilterLocation.countryCode
      );
    }
    
    // Then apply search term filtering
    if (searchValue) {
      filteredLocs = filteredLocs.filter(location => {
        const searchLower = searchValue.toLowerCase();
        return (
          location.name.toLowerCase().includes(searchLower) ||
          location.country.toLowerCase().includes(searchLower)
        );
      });
    }
    
    return filteredLocs;
  };



  const handleOpenDetails = (location) => {
    setSelectedLocation(location);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
  };

  const handleSetFilterLocation = (location) => {
    setSelectedFilterLocation(location);
    setViewMode('location');
    setDetailsOpen(false);
  };


  // Handle opening the edit location dialog
const handleOpenEditLocation = (location) => {
  // Set the location data for editing
  setEditLocation({
    Gigs_id: location.id,
    Gigs_name: location.global_location_name || location.name,
    Organizational_Code: location.organizationalCode || orgCode
  });
  setEditLocationOpen(true);
};

// Handle closing the edit location dialog
const handleCloseEditLocation = () => {
  setEditLocationOpen(false);
  // Reset the form
  setEditLocation({
    Gigs_id: '',
    Gigs_name: '',
    Organizational_Code: ''
  });
};

// Handle edit location input changes
const handleEditInputChange = (e) => {
  const { name, value } = e.target;
  setEditLocation(prev => ({ ...prev, [name]: value }));
};

// Edit location with enhanced validation and token handling
// Update location with enhanced validation and token handling
const updateLocation = async () => {
  // Validate form
  if (!editLocation.Gigs_name) {
    setNotification({
      open: true,
      message: 'Please enter a location name',
      severity: 'warning'
    });
    return;
  }
  
  if (!editLocation.Organizational_Code) {
    setNotification({
      open: true,
      message: 'Please enter an organization code',
      severity: 'warning'
    });
    return;
  }
  
  if (!editLocation.Gigs_id) {
    setNotification({
      open: true,
      message: 'Location ID is missing',
      severity: 'error'
    });
    return;
  }
  
  // Make sure token is available
  if (!token) {
    console.error('Authorization token is missing');
    setNotification({
      open: true,
      message: 'Authorization token is missing. Please log in again.',
      severity: 'error'
    });
    return;
  }

  // Ensure token is properly formatted with Bearer prefix if needed
  // const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  
  setIsLoading(true);
  try {
    // Prepare payload with all required fields using the correct format
    const payload = {
      global_id: editLocation.Gigs_id,
      global_location_name: editLocation.Gigs_name
    };
    
    const response = await axios.put(API_ENDPOINTS.EDIT_LOCATION, payload, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (response.data) {
      setNotification({
        open: true,
        message: 'Location updated successfully',
        severity: 'success'
      });
      
      // Refresh locations list
      fetchLocations(editLocation.Organizational_Code);
      
      // Close the modal
      setEditLocationOpen(false);
      
      // Reset form
     setEditLocation({
  global_id: '',
  global_location_name: '',
  Organizational_Code: ''
});
    } else {
      throw new Error('No data returned from API');
    }
  } catch (error) {
    console.error('Error updating location:', error);
    setNotification({
      open: true,
      message: `Error updating location: ${error.message}`,
      severity: 'error'
    });
  } finally {
    setIsLoading(false);
  }
};
// Delete location function
const deleteLocation = async (locationId) => {
  if (!locationId) {
    setNotification({
      open: true,
      message: 'Location ID is missing',
      severity: 'error'
    });
    return;
  }
  
  // Confirm before deleting
  const confirmed = window.confirm('Are you sure you want to delete this location?');
  if (!confirmed) {
    return;
  }
  
  // Make sure token is available
  if (!token) {
    console.error('Authorization token is missing');
    setNotification({
      open: true,
      message: 'Authorization token is missing. Please log in again.',
      severity: 'error'
    });
    return;
  }

  // Ensure token is properly formatted with Bearer prefix if needed
  // const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  console.log("token",token)
  setIsLoading(true);
  try {
    const response = await axios.post(
      API_ENDPOINTS.DELETE_LOCATION, 
      { global_id: locationId },  // This is the data payload
      {  // This is the config object with headers
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (response.data) {
      setNotification({
        open: true,
        message: 'Location deleted successfully',
        severity: 'success'
      });
      
      // Close location details dialog if open
      if (detailsOpen && selectedLocation && selectedLocation.id === locationId) {
        setDetailsOpen(false);
      }
      
      // Refresh locations list
      fetchLocations(orgCode);
    } else {
      throw new Error('No data returned from API');
    }
  } catch (error) {
    console.error('Error deleting location:', error);
    setNotification({
      open: true,
      message: `Error deleting location: ${error.message}`,
      severity: 'error'
    });
  } finally {
    setIsLoading(false);
  }
};
  const handleOpenNewLocation = () => {
    const clientCode = user?.data?.[0]?.client_code || orgCode;
    
    if (clientCode?.trim()) {
      // Pre-fill the organization code in the form
      setNewLocation(prev => ({
        ...prev,
        Organizational_Code: clientCode
      }));
    }
    setNewLocationOpen(true);
  };

  const handleCloseNewLocation = () => {
    setNewLocationOpen(false);
    // Reset the form
    setNewLocation({
      global_location_name: '',
      Organizational_Code: ''
    });
  };

  // Handle view mode changes
  

  const handleOrgCodeChange = (e) => {
    const orgCode = e.target.value;
    setNewLocation(prev => ({ ...prev, Organizational_Code: orgCode }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewLocation(prev => ({ ...prev, [name]: value }));
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // Toggle help dialog
  const handleToggleHelp = () => {
    setHelpOpen(!helpOpen);
  };

  return (
    <Box sx={{ 
      bgcolor: '#050a18', 
      color: 'white',
      minHeight: '100vh',
      overflow: 'auto',
    }}>
      <Box sx={{ 
        position: 'relative',
        height: '100vh',
      }}>
        {/* Help button */}
        <IconButton
          onClick={handleToggleHelp}
          sx={{
            position: 'absolute',
            top: 20,
            right: 20,
            zIndex: 100,
            color: '#00e5ff',
            bgcolor: 'rgba(0, 229, 255, 0.1)',
            '&:hover': {
              bgcolor: 'rgba(0, 229, 255, 0.2)',
            }
          }}
        >
          <InfoIcon />
        </IconButton>
        
        {/* Help dialog */}
        <Dialog
          open={helpOpen}
          onClose={handleToggleHelp}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            style: {
              backgroundColor: '#0c1629',
              color: 'white',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6)',
              border: '1px solid rgba(0, 229, 255, 0.2)',
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            bgcolor: '#050a18',
            borderBottom: '1px solid rgba(0, 229, 255, 0.1)',
            p: 2
          }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              GlobalLens Help
            </Typography>
            <IconButton 
              edge="end" 
              color="inherit" 
              onClick={handleToggleHelp} 
              aria-label="close"
              sx={{ 
                bgcolor: 'rgba(255, 255, 255, 0.05)',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3, bgcolor: '#0c1629' }}>
            <Typography variant="h6" gutterBottom>
              Location Names
            </Typography>
            <Typography paragraph>
              When entering location names, you can use any city or country name. The system will automatically
              map recognized locations to their geographical positions on the world map.
            </Typography>
            <Typography paragraph>
              For example, you can enter city names like "Tokyo", "New York", "Hyderabad", or country names 
              like "India", "USA", "Australia".
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Search
            </Typography>
            <Typography paragraph>
              Use the search box to filter locations by name or country.
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Adding New Locations
            </Typography>
            <Typography paragraph>
              To add a new location, click the "+" button and fill in the required information.
              You need to provide a location name and organization code.
            </Typography>
          </DialogContent>
        </Dialog>
        
        {/* Top search and controls bar */}
        <Box sx={{ 
          position: 'absolute',
          top: 80, 
          left: 0, 
          width: '100%', 
          display: 'flex',
          justifyContent: 'center',
          zIndex: 50,
        }}>
          <Paper sx={{ 
            p: 1.5, 
            width: '90%', 
            maxWidth: 1000, 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            bgcolor: 'rgba(13, 26, 48, 0.9)',
            border: '1px solid rgba(0, 229, 255, 0.2)',
            backdropFilter: 'blur(8px)',
            borderRadius: 2,
            gap: 2
          }}>
            {/* View mode selector */}
            {/* <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                size="small"
                sx={{ 
                  mr: 2,
                  '& .MuiToggleButton-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    borderColor: 'rgba(0, 229, 255, 0.2)',
                    '&.Mui-selected': {
                      color: '#00e5ff',
                      bgcolor: 'rgba(0, 229, 255, 0.1)',
                    },
                    '&:hover': {
                      bgcolor: 'rgba(0, 229, 255, 0.05)',
                    }
                  }
                }}
              >
                <ToggleButton value="global">
                  <Tooltip title="Global View">
                    <GlobalIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton 
                  value="location" 
                  disabled={!selectedFilterLocation}
                >
                  <Tooltip title="Location View">
                    <LocationIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
              
            
              <Typography
                variant="body2"
                color="white"
                sx={{ fontWeight: 'medium' }}
              >
                {viewMode === 'global' ? (
                  <>Showing all locations <Chip size="small" label={locations.length} sx={{ ml: 1, height: 20 }} /></>
                ) : (
                  <>Filtering by: <span style={{ color: '#00e5ff' }}>{selectedFilterLocation?.name || 'None'}</span></>
                )}
              </Typography>
            </Box> */}
            
            {/* Divider */}
            <Divider orientation="vertical" flexItem sx={{ 
              bgcolor: 'rgba(0, 229, 255, 0.2)', 
              display: { xs: 'none', sm: 'block' } 
            }} />
            <Divider sx={{ 
              bgcolor: 'rgba(0, 229, 255, 0.2)', 
              width: '100%', 
              my: 1, 
              display: { xs: 'block', sm: 'none' } 
            }} />
            
            {/* Search */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: 1,
              flexGrow: 1,
              width: { xs: '100%', sm: 'auto' }
            }}>
              <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', ml: 1 }} />
              <TextField
                variant="standard"
                placeholder="Search locations..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                size="small"
                fullWidth
                sx={{ 
                  input: { color: 'white' },
                  '& .MuiInput-underline:before': {
                    borderBottomColor: 'rgba(0, 229, 255, 0.2)',
                  },
                  '& .MuiInput-underline:hover:before': {
                    borderBottomColor: 'rgba(0, 229, 255, 0.5)',
                  },
                  '& .MuiInput-underline:after': {
                    borderBottomColor: '#00e5ff',
                  },
                }}
              />
              {searchValue && (
                <IconButton 
                  size="small" 
                  onClick={() => setSearchValue('')}
                  sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
              
              <IconButton 
                color="primary" 
                onClick={handleOpenNewLocation}
                sx={{
                  ml: 1,
                  color: '#00e5ff',
                  bgcolor: 'rgba(0, 229, 255, 0.1)',
                  '&:hover': {
                    bgcolor: 'rgba(0, 229, 255, 0.2)',
                  }
                }}
              >
                <AddIcon />
              </IconButton>
            </Box>
          </Paper>
        </Box>
        
        {/* Main title */}
        <Box sx={{ 
          position: 'absolute', 
          top: 20, 
          left: 0, 
          width: '100%', 
          textAlign: 'center',
          zIndex: 20,
        }}>
          <Typography variant="h4" component="h1" sx={{ 
            fontWeight: 'bold', 
            textShadow: '0 0 10px rgba(0, 0, 0, 0.7)',
            letterSpacing: '1px'
          }}>
            Global IT Operations
          </Typography>
        </Box>
        
        {/* World Map Visualization */}
        <Box sx={{ 
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0,
          marginTop: '4rem'
        }}>
      <GlobeVisualization 
  locations={getFilteredLocations()} 
  onLocationSelect={handleOpenDetails}
  searchValue={searchValue}
  currentTime={currentTime}
  onEditLocation={handleOpenEditLocation}
  onDeleteLocation={deleteLocation}
/>
        </Box>
        
        {/* Empty state message when no locations */}
        {locations.length === 0 && !isLoading && (
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            zIndex: 10,
            p: 4,
            bgcolor: 'rgba(13, 26, 48, 0.8)',
            borderRadius: 2,
            border: '1px solid rgba(0, 229, 255, 0.2)',
            width: '80%',
            maxWidth: '500px'
          }}>
            <Typography variant="h6" gutterBottom>
              No locations found
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.7)' }}>
              Enter an Organization Code to search for locations or add a new location using the "+" button.
            </Typography>
            <Button 
              variant="contained" 
              onClick={handleOpenNewLocation}
              startIcon={<AddIcon />}
              sx={{
                bgcolor: '#00e5ff',
                color: '#050a18',
                '&:hover': {
                  bgcolor: '#00b8d4',
                }
              }}
            >
              Add New Location
            </Button>
          </Box>
        )}
        
        {/* No results message when filtering returns no locations */}
        {locations.length > 0 && getFilteredLocations().length === 0 && (
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            zIndex: 10,
            p: 4,
            bgcolor: 'rgba(13, 26, 48, 0.8)',
            borderRadius: 2,
            border: '1px solid rgba(0, 229, 255, 0.2)',
          }}>
            <Typography variant="h6" gutterBottom>
              No matching locations
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              No locations match your search for "{searchValue}".
            </Typography>
            <Button 
              variant="outlined" 
              onClick={() => setSearchValue('')}
              sx={{
                mt: 2,
                color: '#00e5ff',
                borderColor: '#00e5ff',
                '&:hover': {
                  borderColor: '#00b8d4',
                  bgcolor: 'rgba(0, 229, 255, 0.1)',
                }
              }}
            >
              Clear Search
            </Button>
          </Box>
        )}
        
        {/* Loading indicator */}
        {isLoading && (
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            zIndex: 10,
            p: 4,
            bgcolor: 'rgba(13, 26, 48, 0.8)',
            borderRadius: 2,
            border: '1px solid rgba(0, 229, 255, 0.2)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
            <CircularProgress 
              size={60} 
              thickness={4} 
              sx={{ 
                color: '#00e5ff', 
                mb: 2 
              }} 
            />
            <Typography variant="h6">
              Loading locations...
            </Typography>
          </Box>
        )}
      </Box>
      
      {/* Details Dialog */}
      <Dialog 
        open={detailsOpen} 
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: '#0c1629',
            color: 'white',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6)',
            border: '1px solid rgba(0, 229, 255, 0.2)',
          }
        }}
      >
        {selectedLocation && (
          <>
            <DialogTitle sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              bgcolor: '#050a18',
              borderBottom: '1px solid rgba(0, 229, 255, 0.1)',
              p: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h6" component="span" sx={{ mr: 1, fontSize: '1.5rem' }}>
                  {selectedLocation.flag}
                </Typography>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {selectedLocation.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    {selectedLocation.country}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                 {/* Edit button */}
                 <div style={{ width: '20px', height: '20px', backgroundColor: 'red' }}></div>
    <Tooltip title="Edit Location">
      <IconButton
        size="small"
        onClick={() => handleOpenEditLocation(selectedLocation)}
        sx={{
          color: '#00e5ff',
          bgcolor: 'rgba(0, 229, 255, 0.1)',
          '&:hover': {
            bgcolor: 'rgba(0, 229, 255, 0.2)',
          }
        }}
      >
        <EditIcon fontSize="small" />
      </IconButton>
    </Tooltip>

    {/* Delete button */}
    <Tooltip title="Delete Location">
      <IconButton
        size="small"
        onClick={() => deleteLocation(selectedLocation.id)}
        sx={{
          color: '#ef4444',
          bgcolor: 'rgba(239, 68, 68, 0.1)',
          '&:hover': {
            bgcolor: 'rgba(239, 68, 68, 0.2)',
          }
        }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Tooltip>

                {viewMode !== 'location' || selectedFilterLocation?.id !== selectedLocation.id ? (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<FilterIcon />}
                    onClick={() => handleSetFilterLocation(selectedLocation)}
                    sx={{
                      borderColor: 'rgba(0, 229, 255, 0.5)',
                      color: '#00e5ff',
                      '&:hover': {
                        borderColor: '#00e5ff',
                        bgcolor: 'rgba(0, 229, 255, 0.1)',
                      }
                    }}
                  >
                    Filter By
                  </Button>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<GlobalIcon />}
                    onClick={() => setViewMode('global')}
                    sx={{
                      borderColor: 'rgba(255, 204, 0, 0.5)',
                      color: '#ffcc00',
                      '&:hover': {
                        borderColor: '#ffcc00',
                        bgcolor: 'rgba(255, 204, 0, 0.1)',
                      }
                    }}
                  >
                    Global View
                  </Button>
                )}
                
                <IconButton 
                  edge="end" 
                  color="inherit" 
                  onClick={handleCloseDetails} 
                  aria-label="close"
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
          
            <DialogContent sx={{ p: 3, bgcolor: '#0c1629' }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Stack spacing={3}>
                    <Paper elevation={0} sx={{ 
                      p: 2.5, 
                      bgcolor: 'rgba(13, 26, 48, 0.6)', 
                      color: 'white',
                      borderRadius: 2,
                      boxShadow: 'none',
                      border: '1px solid rgba(0, 229, 255, 0.1)'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <ClockIcon sx={{ mr: 1, color: '#00e5ff' }} />
                        <Typography variant="h6">Local Time</Typography>
                      </Box>
                      <Typography variant="h4" sx={{ fontFamily: 'monospace', color: '#00e5ff' }}>
                        {currentTime[selectedLocation.name] || "Loading..."} {selectedLocation.timezoneName}
                      </Typography>
                    </Paper>
                    
                    <Paper elevation={0} sx={{ 
                      p: 2.5, 
                      bgcolor: 'rgba(13, 26, 48, 0.6)', 
                      color: 'white',
                      borderRadius: 2,
                      boxShadow: 'none',
                      border: '1px solid rgba(0, 229, 255, 0.1)'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PeopleIcon sx={{ mr: 1, color: '#00e5ff' }} />
                        <Typography variant="h6">Staff</Typography>
                      </Box>
                      <Typography variant="h4" sx={{ color: '#00e5ff' }}>
                        {selectedLocation.details.employees} <Typography variant="body2" component="span">employees</Typography>
                      </Typography>
                    </Paper>
                    
                    <Paper elevation={0} sx={{ 
                      p: 2.5, 
                      bgcolor: 'rgba(13, 26, 48, 0.6)', 
                      color: 'white',
                      borderRadius: 2,
                      boxShadow: 'none',
                      border: '1px solid rgba(0, 229, 255, 0.1)'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <AlertIcon sx={{ mr: 1, color: '#00e5ff' }} />
                        <Typography variant="h6">Incidents</Typography>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Box sx={{ 
                            p: 2, 
                            borderRadius: 2, 
                            bgcolor: 'rgba(8, 17, 34, 0.7)',
                            border: '1px solid rgba(239, 68, 68, 0.2)'
                          }}>
                            <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">Active</Typography>
                            <Typography variant="h5" color="#EF4444" sx={{ fontWeight: 'bold' }}>
                              {selectedLocation.details.activeIncidents}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{ 
                            p: 2, 
                            borderRadius: 2, 
                            bgcolor: 'rgba(8, 17, 34, 0.7)',
                            border: '1px solid rgba(16, 185, 129, 0.2)'
                          }}>
                            <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">Resolved</Typography>
                            <Typography variant="h5" color="#10B981" sx={{ fontWeight: 'bold' }}>
                              {selectedLocation.details.resolvedIncidents}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Stack>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Stack spacing={3}>
                    <Paper elevation={0} sx={{ 
                      p: 2.5, 
                      bgcolor: 'rgba(13, 26, 48, 0.6)', 
                      color: 'white',
                      borderRadius: 2,
                      boxShadow: 'none',
                      border: '1px solid rgba(0, 229, 255, 0.1)'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <TrendingUpIcon sx={{ mr: 1, color: '#00e5ff' }} />
                        <Typography variant="h6">Initiatives</Typography>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Box sx={{ 
                            p: 2, 
                            borderRadius: 2, 
                            bgcolor: 'rgba(8, 17, 34, 0.7)',
                            border: '1px solid rgba(59, 130, 246, 0.2)'
                          }}>
                            <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">Ongoing</Typography>
                            <Typography variant="h5" color="#3B82F6" sx={{ fontWeight: 'bold' }}>
                              {selectedLocation.details.ongoingInitiatives}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{ 
                            p: 2, 
                            borderRadius: 2, 
                            bgcolor: 'rgba(8, 17, 34, 0.7)',
                            border: '1px solid rgba(16, 185, 129, 0.2)'
                          }}>
                            <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">Completed</Typography>
                            <Typography variant="h5" color="#10B981" sx={{ fontWeight: 'bold' }}>
                              {selectedLocation.details.completedInitiatives}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>
                    
                    {/* Upcoming Milestones Section */}
                    <Paper elevation={0} sx={{ 
                      p: 2.5, 
                      bgcolor: 'rgba(13, 26, 48, 0.6)', 
                      color: 'white',
                      borderRadius: 2,
                      boxShadow: 'none',
                      border: '1px solid rgba(0, 229, 255, 0.1)'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <EventIcon sx={{ mr: 1, color: '#00e5ff' }} />
                        <Typography variant="h6">Upcoming Milestones</Typography>
                      </Box>
                      <Stack spacing={2}>
                        {selectedLocation.milestones.map((milestone) => (
                          <Box 
                            key={milestone.id}
                            sx={{ 
                              p: 2, 
                              borderRadius: 2, 
                              bgcolor: 'rgba(8, 17, 34, 0.7)',
                              border: '1px solid rgba(0, 229, 255, 0.1)',
                            }}
                          >
                            <Box sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              mb: 1
                            }}>
                              <Typography variant="body1" fontWeight="medium">
                                {milestone.name}
                              </Typography>
                              <Chip 
                                label={milestone.status}
                                size="small"
                                sx={{ 
                                  bgcolor: `${milestone.statusColor}20`, 
                                  color: milestone.statusColor,
                                  fontWeight: 'bold',
                                  border: `1px solid ${milestone.statusColor}40`
                                }}
                              />
                            </Box>
                            <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" sx={{ mb: 1 }}>
                              {milestone.description}
                            </Typography>
                            <Box sx={{
                              display: 'flex',
                              justifyContent: 'flex-start',
                              alignItems: 'center'
                            }}>
                              <EventIcon sx={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.5)', mr: 0.5 }} />
                              <Typography variant="caption" color="rgba(255, 255, 255, 0.5)">
                                Due: {milestone.dueDate}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                        {selectedLocation.milestones.length === 0 && (
                          <Box sx={{ 
                            p: 2, 
                            borderRadius: 2, 
                            bgcolor: 'rgba(8, 17, 34, 0.7)',
                            border: '1px solid rgba(0, 229, 255, 0.1)',
                            textAlign: 'center'
                          }}>
                            <Typography color="rgba(255, 255, 255, 0.5)">
                              No upcoming milestones
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </Paper>
                    
                    {/* Risks Section */}
                    <Paper elevation={0} sx={{ 
                      p: 2.5, 
                      bgcolor: 'rgba(13, 26, 48, 0.6)', 
                      color: 'white',
                      borderRadius: 2,
                      boxShadow: 'none',
                      border: '1px solid rgba(0, 229, 255, 0.1)',
                      mt: 3
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <RiskIcon sx={{ mr: 1, color: '#00e5ff' }} />
                        <Typography variant="h6">Risks</Typography>
                      </Box>
                      <Stack spacing={2}>
                        {selectedLocation.risks.map((risk) => (
                          <Box 
                            key={risk.id}
                            sx={{ 
                              p: 2, 
                              borderRadius: 2, 
                              bgcolor: 'rgba(8, 17, 34, 0.7)',
                              border: '1px solid rgba(0, 229, 255, 0.1)',
                            }}
                          >
                            <Box sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              mb: 1
                            }}>
                              <Typography variant="body1" fontWeight="medium">
                                {risk.name}
                              </Typography>
                              <Chip 
                                label={risk.severity}
                                size="small"
                                sx={{ 
                                  bgcolor: `${risk.severityColor}20`, 
                                  color: risk.severityColor,
                                  fontWeight: 'bold',
                                  border: `1px solid ${risk.severityColor}40`
                                }}
                              />
                            </Box>
                            <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" sx={{ mb: 1 }}>
                              {risk.description}
                            </Typography>
                            <Box sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <Typography variant="caption" fontWeight="medium">
                                Impact: {risk.impact}
                              </Typography>
                              <Typography variant="caption" color="rgba(255, 255, 255, 0.6)">
                                {risk.mitigationPlan}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                        {selectedLocation.risks.length === 0 && (
                          <Box sx={{ 
                            p: 2, 
                            borderRadius: 2, 
                            bgcolor: 'rgba(8, 17, 34, 0.7)',
                            border: '1px solid rgba(0, 229, 255, 0.1)',
                            textAlign: 'center'
                          }}>
                            <Typography color="rgba(255, 255, 255, 0.5)">
                              No active risks
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </Paper>
                  </Stack>
                </Grid>
              </Grid>

              {/* API Data Info */}
              <Paper elevation={0} sx={{ 
                mt: 3,
                p: 2.5, 
                bgcolor: 'rgba(13, 26, 48, 0.6)', 
                color: 'white',
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid rgba(0, 229, 255, 0.1)'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LocationIcon sx={{ mr: 1, color: '#00e5ff' }} />
                  <Typography variant="h6">Location Information</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      bgcolor: 'rgba(8, 17, 34, 0.7)',
                      height: '100%'
                    }}>
                      <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">Org. Code</Typography>
                      <Typography variant="body1" sx={{ mt: 1, wordBreak: 'break-all' }}>
                        {selectedLocation.organizationalCode}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      bgcolor: 'rgba(8, 17, 34, 0.7)',
                      height: '100%'
                    }}>
                      <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">Geographic Coordinates</Typography>
                      <Typography variant="body1" sx={{ mt: 1 }}>
                        {selectedLocation.lat?.toFixed(4) || "N/A"}, {selectedLocation.lng?.toFixed(4) || "N/A"}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* New Location Dialog */}
      <Dialog 
        open={newLocationOpen} 
        onClose={handleCloseNewLocation}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: '#0c1629',
            color: 'white',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6)',
            border: '1px solid rgba(0, 229, 255, 0.2)',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: '#050a18',
          borderBottom: '1px solid rgba(0, 229, 255, 0.1)',
          p: 2
        }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Add New Location
          </Typography>
          <IconButton 
            edge="end" 
            color="inherit" 
            onClick={handleCloseNewLocation} 
            aria-label="close"
            sx={{ 
              bgcolor: 'rgba(255, 255, 255, 0.05)',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3, bgcolor: '#0c1629' }}>
          <Box component="form" sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="global_location_name"
              label="Location Name"
              name="global_location_name"
              value={newLocation.global_location_name}
              onChange={handleInputChange}
              helperText="Enter a city or country name (e.g., 'Tokyo', 'New York', 'India')"
              sx={{ 
                mb: 3,
                input: { color: 'white' },
                '& .MuiFormLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiFormHelperText-root': {
                  color: 'rgba(255, 255, 255, 0.5)',
                },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(0, 229, 255, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(0, 229, 255, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#00e5ff',
                  },
                }
              }}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              id="Organizational_Code"
              label="Organization Code"
              name="Organizational_Code"
              value={newLocation.Organizational_Code}
              onChange={handleOrgCodeChange}
              sx={{ 
                mb: 3,
                input: { color: 'white' },
                '& .MuiFormLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(0, 229, 255, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(0, 229, 255, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#00e5ff',
                  },
                }
              }}
            />
            
            <Button
              fullWidth
              variant="contained"
              onClick={createLocation}
              disabled={isLoading || !newLocation.global_location_name || !newLocation.Organizational_Code}
              sx={{ 
                mt: 3, 
                mb: 2,
                bgcolor: '#00e5ff',
                color: '#050a18',
                '&:hover': {
                  bgcolor: '#00b8d4',
                },
                '&.Mui-disabled': {
                  bgcolor: 'rgba(0, 229, 255, 0.3)',
                  color: 'rgba(255, 255, 255, 0.4)',
                }
              }}
            >
              Add Location
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      {/* Edit Location Dialog */}
<Dialog 
  open={editLocationOpen} 
  onClose={handleCloseEditLocation}
  maxWidth="sm"
  fullWidth
  PaperProps={{
    style: {
      backgroundColor: '#0c1629',
      color: 'white',
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6)',
      border: '1px solid rgba(0, 229, 255, 0.2)',
    }
  }}
>
  <DialogTitle sx={{ 
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'center',
    bgcolor: '#050a18',
    borderBottom: '1px solid rgba(0, 229, 255, 0.1)',
    p: 2
  }}>
    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
      Edit Location
    </Typography>
    <IconButton 
      edge="end" 
      color="inherit" 
      onClick={handleCloseEditLocation} 
      aria-label="close"
      sx={{ 
        bgcolor: 'rgba(255, 255, 255, 0.05)',
        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
      }}
    >
      <CloseIcon />
    </IconButton>
  </DialogTitle>
  
  <DialogContent sx={{ p: 3, bgcolor: '#0c1629' }}>
    <Box component="form" sx={{ mt: 1 }}>
      <TextField
        margin="normal"
        required
        fullWidth
        id="Gigs_name"
        label="Location Name"
        name="Gigs_name"
        value={editLocation.Gigs_name}
        onChange={handleEditInputChange}
        helperText="Enter a city or country name (e.g., 'Tokyo', 'New York', 'India')"
        sx={{ 
          mb: 3,
          input: { color: 'white' },
          '& .MuiFormLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)',
          },
          '& .MuiFormHelperText-root': {
            color: 'rgba(255, 255, 255, 0.5)',
          },
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: 'rgba(0, 229, 255, 0.2)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(0, 229, 255, 0.5)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#00e5ff',
            },
          }
        }}
      />
      
      <TextField
        margin="normal"
        required
        fullWidth
        id="Organizational_Code"
        label="Organization Code"
        name="Organizational_Code"
        value={editLocation.Organizational_Code}
        onChange={handleEditInputChange}
        sx={{ 
          mb: 3,
          input: { color: 'white' },
          '& .MuiFormLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)',
          },
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: 'rgba(0, 229, 255, 0.2)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(0, 229, 255, 0.5)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#00e5ff',
            },
          }
        }}
      />
      
      {/* Hidden input for Gigs_id */}
      <input type="hidden" name="Gigs_id" value={editLocation.Gigs_id} />
      
      <Button
  fullWidth
  variant="contained"
  onClick={updateLocation}  // Changed from editLocation to updateLocation
  disabled={isLoading || !editLocation.Gigs_name || !editLocation.Organizational_Code}
  sx={{ 
    mt: 3, 
    mb: 2,
    bgcolor: '#00e5ff',
    color: '#050a18',
    '&:hover': {
      bgcolor: '#00b8d4',
    },
    '&.Mui-disabled': {
      bgcolor: 'rgba(0, 229, 255, 0.3)',
      color: 'rgba(255, 255, 255, 0.4)',
    }
  }}
>
  Update Location
</Button>
    </Box>
  </DialogContent>
</Dialog>
      {/* Notification snackbar */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity} 
          sx={{ width: '100%', bgcolor: notification.severity === 'success' ? '#10B981' : (notification.severity === 'error' ? '#EF4444' : '#F59E0B'), color: 'white' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
      
      {/* Add CSS for animations */}
      <style jsx global>{`
        @keyframes pulse {
          0% { transform: scale(0.95); opacity: 0.8; }
          70% { transform: scale(1.1); opacity: 0.3; }
          100% { transform: scale(0.95); opacity: 0.8; }
        }
      `}</style>
    </Box>
  );
};

export default GlobalLens;